import { memo, useCallback } from 'react';
import {
  ToolbarClearIcons,
  ToolbarSearchIcons,
} from '../../../../../../icons/icons';
import { ExportButton } from './ExportButton';
import { GridViewControls } from './GridViewControls';
import { ToolbarProps } from './interfaces';
import { RefreshButton } from './RefreshButton';
import { TableViewControls } from './TableViewControls';
import { UnsuccessfulApplicantsActions } from './UnsuccessfulApplicantsActions';
import { ViewModeToggle } from './ViewModeToggle';
import { Box, Divider, IconButton, InputBase, Paper, Stack, Tooltip, Typography } from '@mui/material';

export const Toolbar = memo<ToolbarProps>((props) => {
  const {
    globalSearchTerm,
    setGlobalSearchTerm,
    viewMode,
    setViewMode,
    isAnalyticalFilter,
    is3dView,
    svgActiveTab,
    setSvgActiveTab,
    view3D,
    setView3D,
    loading,
    currentTab,
    selectAllUnsuccessful,
    handleColumnConfig,
    handleClearSelected,
    isClearDisabled,
    handleSendEmailToSelected,
    isSendEmailDisabled,
    selectedApplicantCount,
    handleExport,
    filteredCandidateCount,
    candidateCount,
    handleAnalyticsFilterOpen,
    isAnalyticsFilterOpen,
    handleToggle3DView,
    handleGraphConfig,
    handleRefreshData,
    showHeadshots,
    setShowHeadshots,
    handleStatusChange,
    handlePreQualFilterOpen,
    isPreQualFilterOpen,
    handleCCAFilterOpen,
    isCCAFilterOpen,
  } = props;

  const clearSearchTerm = useCallback(() => {
    setGlobalSearchTerm('');
  }, [setGlobalSearchTerm]);

  const toggleSvgTab = useCallback(() => {
    setSvgActiveTab(svgActiveTab === 3 ? -1 : 3);
  }, [setSvgActiveTab, svgActiveTab]);

  const toggle3DViewWithReset = useCallback(() => {
    setView3D(!view3D);
    setSvgActiveTab(-1);
  }, [setView3D, view3D, setSvgActiveTab]);

  const handleHeadshotToggle = useCallback(() => {
    setShowHeadshots(!showHeadshots);
  }, [setShowHeadshots, showHeadshots]);

  const isUnsuccessfulTab = currentTab === 'Unsuccessful Applicants';

  return (
    <Paper
      elevation={0}
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 20,
        borderRadius: '8px',
        border: '1px solid',
        borderColor: 'rgba(226,232,240,0.7)',
        bgcolor: 'rgba(255,255,255,0.95)',
        boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
        backdropFilter: 'blur(12px)',
      }}
    >
      {/* Unified Ultra-Compact Toolbar Row */}
      <Box
        sx={{
          overflowX: 'auto',
          px: { xs: 0.75, sm: 1 },
          py: 0.75,
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.75}
          sx={{ minWidth: 'max-content', flexWrap: 'nowrap' }}
        >
          {/* Search Section */}
          <Box
            sx={{
              position: 'relative',
              width: { xs: 180, sm: 240, lg: 280 },
              flexShrink: 0,
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                right: 'auto',
                pl: 1.25,
                display: 'flex',
                alignItems: 'center',
                pointerEvents: 'none',
              }}
            >
              <ToolbarSearchIcons size={12} color="#94a3b8" />
            </Box>
            <InputBase
              type="text"
              placeholder="Search..."
              value={globalSearchTerm}
              onChange={(e) => setGlobalSearchTerm(e.target.value)}
              inputProps={{ 'aria-label': 'Search candidates' }}
              sx={{
                height: 32,
                width: '100%',
                borderRadius: '8px',
                border: '1px solid',
                borderColor: '#e2e8f0',
                bgcolor: '#f8fafc',
                pl: 4,
                pr: 3.5,
                fontSize: '12px',
                transition: 'all 0.2s',
                '& input::placeholder': { color: '#94a3b8' },
                '&.Mui-focused': {
                  borderColor: '#34d399',
                  bgcolor: 'white',
                  boxShadow: '0 0 0 2px rgba(16,185,129,0.15)',
                },
              }}
            />
            {globalSearchTerm && (
              <Tooltip title="Clear search">
                <IconButton
                  onClick={clearSearchTerm}
                  size="small"
                  sx={{
                    position: 'absolute',
                    right: 0,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    mr: 0.5,
                    color: '#94a3b8',
                    '&:hover': { color: '#ef4444' },
                    transition: 'color 0.2s',
                  }}
                >
                  <ToolbarClearIcons size={10} />
                </IconButton>
              </Tooltip>
            )}
          </Box>

          <Divider
            orientation="vertical"
            flexItem
            sx={{ height: 20, alignSelf: 'center', bgcolor: '#e2e8f0' }}
          />

          {/* Main Controls */}
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>
            {viewMode === 'grid' ? (
              <GridViewControls
                isAnalyticalFilter={isAnalyticalFilter}
                isAnalyticsFilterOpen={isAnalyticsFilterOpen}
                handleAnalyticsFilterOpen={handleAnalyticsFilterOpen}
                is3dView={is3dView}
                handleToggle3DView={handleToggle3DView}
                handleGraphConfig={handleGraphConfig}
                svgActiveTab={svgActiveTab}
                view3D={view3D}
                toggleSvgTab={toggleSvgTab}
                loading={loading}
                toggle3DViewWithReset={toggle3DViewWithReset}
                isAnalytical3D={props.isAnalytical3D}
                showHeadshots={showHeadshots}
                handleHeadshotToggle={handleHeadshotToggle}
                currentTab={currentTab}
                handleStatusChange={handleStatusChange}
                handlePreQualFilterOpen={handlePreQualFilterOpen}
                isPreQualFilterOpen={isPreQualFilterOpen}
                handleCCAFilterOpen={handleCCAFilterOpen}
                isCCAFilterOpen={isCCAFilterOpen}
              />
            ) : (
              <TableViewControls handleColumnConfig={handleColumnConfig} />
            )}

            <RefreshButton onRefresh={handleRefreshData} loading={loading} />
          </Stack>

          {/* Bulk Actions - Now Inline */}
          {isUnsuccessfulTab && (
            <>
              <Divider
                orientation="vertical"
                flexItem
                sx={{ height: 20, alignSelf: 'center', bgcolor: '#e2e8f0' }}
              />
              <UnsuccessfulApplicantsActions
                selectAllUnsuccessful={selectAllUnsuccessful}
                handleClearSelected={handleClearSelected}
                isClearDisabled={isClearDisabled}
                handleSendEmailToSelected={handleSendEmailToSelected}
                isSendEmailDisabled={isSendEmailDisabled}
                selectedApplicantCount={selectedApplicantCount}
              />
            </>
          )}

          <Divider
            orientation="vertical"
            flexItem
            sx={{ height: 20, alignSelf: 'center', bgcolor: '#e2e8f0' }}
          />

          {/* Right Side Actions */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.75}
            sx={{ ml: 'auto', flexShrink: 0 }}
          >
            <ExportButton handleExport={handleExport} />
            <Divider
              orientation="vertical"
              flexItem
              sx={{ height: 20, alignSelf: 'center', bgcolor: '#e2e8f0' }}
            />
            <ViewModeToggle viewMode={viewMode} setViewMode={setViewMode} />
          </Stack>
        </Stack>
      </Box>

      {/* Minimal status info / Legend overlay */}
      {globalSearchTerm && (
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{
            borderTop: '1px solid',
            borderColor: 'grey.100',
            px: 2,
            pb: 1,
            pt: 0.5,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={0.75}>
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                bgcolor: filteredCandidateCount > 0 ? '#10b981' : '#ef4444',
              }}
            />
            <Typography
              sx={{
                fontSize: '9px',
                fontWeight: 500,
                color: '#94a3b8',
              }}
            >
              {filteredCandidateCount > 0
                ? `${filteredCandidateCount}/${candidateCount}`
                : '0 results'}
            </Typography>
          </Stack>
        </Stack>
      )}
    </Paper>
  );
});

Toolbar.displayName = 'Toolbar';

export default Toolbar;
